import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Plus,
  Bot,
  Trash2,
  Eye,
  ChevronDown,
  ChevronUp,
  Loader2,
  Link2,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VoiceAgentsPage() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [expandedAgents, setExpandedAgents] = useState({});
  const [creating, setCreating] = useState(false);

  const [newAgent, setNewAgent] = useState({
    name: '',
    prompt: '',
    analysis_prompt: '',
    description: ''
  });

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/voice-agents`);
      setAgents(response.data);
    } catch (error) {
      toast.error('Failed to fetch voice agents');
    } finally {
      setLoading(false);
    }
  };

  const createAgent = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await axios.post(`${API}/voice-agents`, newAgent);
      setAgents([response.data, ...agents]);
      setNewAgent({ name: '', prompt: '', analysis_prompt: '', description: '' });
      setCreateDialogOpen(false);
      toast.success('Voice agent created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create voice agent');
    } finally {
      setCreating(false);
    }
  };

  const [deleting, setDeleting] = useState(null);

  const deleteAgent = async (id) => {
    if (!window.confirm('Are you sure you want to delete this voice agent? Campaigns using this agent will keep their existing configuration.')) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/voice-agents/${id}`);
      setAgents(agents.filter(a => a.id !== id));
      toast.success('Voice agent deleted');
    } catch (error) {
      const detail = error.response?.data?.detail || error.response?.data?.message || 'Failed to delete voice agent';
      toast.error(detail);
      console.error('Delete agent error:', error.response?.data);
    } finally {
      setDeleting(null);
    }
  };

  const toggleExpand = (agentId) => {
    setExpandedAgents(prev => ({ ...prev, [agentId]: !prev[agentId] }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-4">
          <Skeleton className="h-8 w-48 bg-gray-100" />
          <Skeleton className="h-64 bg-gray-100 rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-gray-900">Voice Agents</h1>
            <p className="text-gray-500 mt-1">Create and manage your AI voice agents</p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                New Voice Agent
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-gray-900">Create Voice Agent</DialogTitle>
                <DialogDescription className="text-gray-500">
                  Define an AI voice agent with a conversation prompt. A workflow will be automatically created in the Scrini call system.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={createAgent} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Agent Name</Label>
                  <Input
                    placeholder="e.g., Real Estate Sales Agent"
                    value={newAgent.name}
                    onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Description (Optional)</Label>
                  <Input
                    placeholder="e.g., Handles cold calls for property sales"
                    value={newAgent.description}
                    onChange={(e) => setNewAgent({ ...newAgent, description: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Conversation Prompt</Label>
                  <Textarea
                    placeholder="e.g., You are a real estate sales agent calling to discuss new property project. Introduce the project, mention key amenities, pricing..."
                    value={newAgent.prompt}
                    onChange={(e) => setNewAgent({ ...newAgent, prompt: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900 min-h-[150px]"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Analysis Prompt (Optional)</Label>
                  <p className="text-xs text-gray-500">
                    Define how AI should analyze completed call transcripts. Leave empty for default analysis.
                  </p>
                  <Textarea
                    placeholder="e.g., Analyze this call. Return JSON with: sentiment, interest_level, intent, next_action..."
                    value={newAgent.analysis_prompt}
                    onChange={(e) => setNewAgent({ ...newAgent, analysis_prompt: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900 min-h-[80px]"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={creating}>
                    {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Create Agent
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Agents List */}
        {agents.length === 0 ? (
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="py-16 text-center">
              <Bot className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No voice agents yet. Create one to use across campaigns!</p>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => setCreateDialogOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Voice Agent
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {agents.map((agent) => (
              <Card key={agent.id} className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <Bot className="w-5 h-5 text-blue-500" />
                        <CardTitle className="font-heading text-lg text-gray-900">{agent.name}</CardTitle>
                        <Badge variant="outline" className={agent.is_active ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-gray-100 text-gray-500 border-gray-300'}>
                          {agent.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        {agent.scrini_trigger_uuid ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">
                            <Link2 className="w-3 h-3 mr-1" />
                            Workflow Linked
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-amber-50 text-amber-600 border-amber-200">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            No Workflow
                          </Badge>
                        )}
                      </div>
                      {agent.description && (
                        <CardDescription className="mt-1 ml-8">{agent.description}</CardDescription>
                      )}
                      <CardDescription className="mt-1 ml-8">
                        Created {format(new Date(agent.created_at), 'MMM d, yyyy')}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-gray-500 hover:text-rose-400"
                        onClick={() => deleteAgent(agent.id)}
                        disabled={deleting === agent.id}
                      >
                        {deleting === agent.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-gray-500"
                        onClick={() => toggleExpand(agent.id)}
                      >
                        {expandedAgents[agent.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                {/* Expanded - Read-only view of agent config */}
                {expandedAgents[agent.id] && (
                  <CardContent className="pt-0">
                    <Separator className="my-4 bg-gray-200" />

                    <div className="space-y-4">
                      {/* Conversation Prompt - Read Only */}
                      <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Eye className="w-4 h-4 text-gray-400" />
                          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Conversation Prompt</p>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{agent.prompt}</p>
                      </div>

                      {/* Analysis Prompt - Read Only */}
                      {agent.analysis_prompt && (
                        <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Eye className="w-4 h-4 text-gray-400" />
                            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Analysis Prompt</p>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{agent.analysis_prompt}</p>
                        </div>
                      )}

                      {/* Scrini Workflow Info */}
                      {agent.scrini_workflow_id && (
                        <div className="p-4 rounded-lg bg-indigo-50 border border-indigo-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Link2 className="w-4 h-4 text-indigo-500" />
                            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">Scrini Workflow</p>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-indigo-700">
                            <span>Workflow ID: <strong>{agent.scrini_workflow_id}</strong></span>
                            {agent.scrini_trigger_uuid && (
                              <span>Trigger: <code className="text-xs bg-indigo-100 px-2 py-0.5 rounded">{agent.scrini_trigger_uuid.substring(0, 12)}...</code></span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Visual Node Representation */}
                      <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                        <p className="text-xs font-semibold uppercase tracking-wider text-blue-600 mb-3">Agent Flow</p>
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="px-3 py-2 rounded-lg bg-white border border-blue-200 text-sm text-blue-700 font-medium shadow-sm">
                            {agent.scrini_trigger_uuid ? 'Trigger' : 'Incoming Call'}
                          </div>
                          <div className="text-blue-300">&rarr;</div>
                          <div className="px-3 py-2 rounded-lg bg-white border border-blue-200 text-sm text-blue-700 font-medium shadow-sm">
                            Voice Agent: {agent.name}
                          </div>
                          <div className="text-blue-300">&rarr;</div>
                          <div className="px-3 py-2 rounded-lg bg-white border border-blue-200 text-sm text-blue-700 font-medium shadow-sm">
                            Conversation
                          </div>
                          <div className="text-blue-300">&rarr;</div>
                          <div className="px-3 py-2 rounded-lg bg-white border border-emerald-200 text-sm text-emerald-700 font-medium shadow-sm">
                            Analysis
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
